import React from 'react';
import { ToolCard, parseDoubleEncoded } from './ToolCard';
import { typography, palette, components, withOpacity } from '../../../../utils/designTokens';

/**
 * MessageList - Displays a list of LLM messages
 * Used in GraphTab attributes panel for Input/Output sections
 *
 * @param {Array} messages - Array of message objects {role, content, tool_calls}
 * @param {string} type - 'input' or 'output' (for styling direction indicator)
 * @param {string} background - Row background color (default: palette.neutrals.panelSecondary)
 */
const MessageList = ({ messages, type, background = palette.neutrals.panelSecondary }) => {
  if (!Array.isArray(messages)) {
    throw new Error('MessageList requires messages prop as an array');
  }

  if (messages.length === 0) {
    return (
      <div style={{
        fontSize: typography.size.xs,
        color: palette.text.muted
      }}>
        No messages found
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {messages.map((msg, index) => {
        const text = messageText(msg);
        return (
          <div
            key={index}
            className="rounded p-2 border"
            style={{
              backgroundColor: background,
              borderColor: palette.borders.subtle
            }}
          >
            {/* Role badge, direction, and content inline */}
            <div>
              <span
                className={`${components.badge.padding} ${components.badge.rounded} ${components.badge.size} ${components.badge.weight} font-mono`}
                style={{
                  backgroundColor: getRoleBgColor(msg.role),
                  color: getRoleColor(msg.role)
                }}
              >
                {msg.role.toUpperCase()}
              </span>
              <span
                className="font-mono mx-1.5"
                style={{
                  fontSize: typography.size.metadata,
                  color: palette.text.muted
                }}
              >
                {type === 'input' ? '→' : '←'}
              </span>
              {text && (
                <span
                  className="whitespace-pre-wrap break-words"
                  style={{
                    fontSize: typography.size.xs,
                    color: palette.text.primary
                  }}
                >
                  {text}
                </span>
              )}
            </div>

            {/* Tool calls */}
          {msg.tool_calls && msg.tool_calls.length > 0 && (
            <div className="mt-2 space-y-2">
              {msg.tool_calls.map(renderToolCall)}
            </div>
          )}
          </div>
        );
      })}
    </div>
  );
};

function messageText(msg) {
  const content = msg?.content ?? msg?.text ?? '';
  const text = contentToText(content);
  if (text) return text;
  if (Array.isArray(msg?.tool_calls) && msg.tool_calls.length > 0) {
    return msg.tool_calls.map(formatToolCallSummary).filter(Boolean).join(', ');
  }
  return '';
}

function contentToText(content) {
  if (content === null || content === undefined) return '';
  if (typeof content === 'string') return content;
  if (typeof content === 'number' || typeof content === 'boolean') return String(content);

  if (Array.isArray(content)) {
    return content
      .map(contentToText)
      .filter(Boolean)
      .join('\n');
  }

  if (typeof content === 'object') {
    if (typeof content.text === 'string') return content.text;
    if (Array.isArray(content.parts)) return content.parts.map(contentToText).filter(Boolean).join('\n');
    if (content.function_call) return `[Function call: ${content.function_call.name || 'function'}]`;
    if (content.function_response) return `[Function response]`;
    if (content.tool_use) return `[Tool use: ${content.tool_use.name || 'tool'}]`;
    if (content.tool_result) return `[Tool result]`;
    return JSON.stringify(content);
  }

  return '';
}

function formatToolCallSummary(toolCall) {
  const name = toolCall?.function?.name || toolCall?.name || toolCall?.tool_call?.function?.name || 'tool';
  return `[Tool call: ${name}]`;
}

function renderToolCall(toolCall, index) {
  const name = toolCall?.function?.name || toolCall?.name || toolCall?.tool_call?.function?.name;
  const args = toolCall?.function?.arguments || toolCall?.arguments || toolCall?.tool_call?.function?.arguments || {};
  if (!name) return null;
  return <ToolCard key={index} name={name} data={parseDoubleEncoded(args)} />;
}

/**
 * Get background color for role badge
 */
function getRoleBgColor(role) {
  switch (role) {
    case 'user':
      return withOpacity(palette.accents.primary.base, 13);
    case 'assistant':
      return withOpacity(palette.accents.warm.base, 13);
    case 'system':
      return withOpacity(palette.accents.secondary.base, 13);
    case 'tool':
      return withOpacity(palette.status.info, 13);
    default:
      return palette.neutrals.borderSubtle;
  }
}

/**
 * Get text color for role badge
 */
function getRoleColor(role) {
  switch (role) {
    case 'user':
      return palette.accents.primary.base;
    case 'assistant':
      return palette.accents.warm.base;
    case 'system':
      return palette.accents.secondary.base;
    case 'tool':
      return palette.status.info;
    default:
      return palette.text.secondary;
  }
}

export default MessageList;
